import { useCallback, useRef } from 'react';
import { useChatStore } from '@/stores/chatStore';
import { useMapStore } from '@/stores/mapStore';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { queryStream, triggerExcelDownload } from '@/api/ares';
import { fetchDashboard } from '@/api/fleet';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useLearningTracking } from './useLearningTracking';
import type { SSEEvent, SSEResultData } from '@/types/ares';
import type { ChatMessage, ClarificationChoice, QuickReply } from '@/types/chat';
import { normalizeChartPayload } from '@/utils/chartPayload';

interface SendQueryOptions {
  displayText?: string;
  clarificationChoice?: ClarificationChoice;
}

const CLARIFICATION_CHOICES = new Set(['working_set', 'all_data', 'root_set']);

function normalizeQuickReplies(value: unknown): QuickReply[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const replies: QuickReply[] = [];
  value.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const data = item as Record<string, unknown>;
    const label = typeof data.label === 'string' ? data.label.trim() : '';
    const query = typeof data.query === 'string' ? data.query.trim() : '';
    const choice = typeof data.choice === 'string' && CLARIFICATION_CHOICES.has(data.choice)
      ? data.choice as ClarificationChoice
      : undefined;

    if (!label || !query) return;
    replies.push({ label, query, choice });
  });

  return replies.length > 0 ? replies : undefined;
}

export function useSSEQuery() {
  const abortRef = useRef<AbortController | null>(null);

  const messages = useChatStore((s) => s.messages);
  const addMessage = useChatStore((s) => s.addMessage);
  const setLoading = useChatStore((s) => s.setLoading);
  const setReasoning = useChatStore((s) => s.setReasoning);
  const setCurrentChart = useChatStore((s) => s.setCurrentChart);
  const setFocusTrip = useChatStore((s) => s.setFocusTrip);
  const setFocusTrips = useChatStore((s) => s.setFocusTrips);
  const setActiveFilters = useChatStore((s) => s.setActiveFilters);
  const setFeatures = useMapStore((s) => s.setFeatures);
  const setRouteData = useMapStore((s) => s.setRouteData);
  const setLivePosition = useMapStore((s) => s.setLivePosition);
  const clearRouteLines = useMapStore((s) => s.clearRouteLines);
  const setChartModalOpen = useUIStore((s) => s.setChartModalOpen);
  const setRoutePanelOpen = useUIStore((s) => s.setRoutePanelOpen);
  const setCurrentExportId = useUIStore((s) => s.setCurrentExportId);
  const getSessionId = useAuthStore((s) => s.getSessionId);
  const updateDashboardStats = useDashboardStore((s) => s.updateStats);
  const { trackExport } = useLearningTracking();

  const refreshDashboardStats = useCallback(async () => {
    try {
      const stats = await fetchDashboard(true);
      updateDashboardStats(stats);
    } catch {
      // Non-blocking: chat results should not fail because KPI refresh failed.
    }
  }, [updateDashboardStats]);

  const sendQuery = useCallback(
    async (query: string, options?: SendQueryOptions) => {
      // Cancel previous request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Close route panel and clear route layers on new query
      setRoutePanelOpen(false);
      setRouteData(null);
      setLivePosition(null);
      clearRouteLines();

      const sessionId = getSessionId();
      const requestGeneration = useChatStore.getState().clearGeneration;
      const isCurrentGeneration = () => (
        useChatStore.getState().clearGeneration === requestGeneration
      );

      // Add user message
      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        content: options?.displayText || query,
        sender: 'user',
        timestamp: new Date().toISOString(),
      };
      addMessage(userMsg);
      setLoading(true);
      setReasoning('');

      let reasoningText = '';

      try {
        await queryStream(
          query,
          sessionId,
          (event: SSEEvent) => {
            if (!isCurrentGeneration()) return;
            switch (event.event) {
              case 'status': {
                const statusData = event.data as { message?: string; stage?: string };
                const stage = statusData.stage || '';
                const statusMsg = statusData.message || stage || '';
                const prefix = stage === 'retrying' ? '\uD83D\uDD04 ' : '';
                setReasoning(prefix + statusMsg);
                break;
              }
              case 'reasoning': {
                const reasoningData = event.data as { text?: string };
                reasoningText += (reasoningData.text || '') + '\n';
                setReasoning(reasoningText);
                break;
              }
              case 'result': {
                const result = event.data as SSEResultData;
                handleResult(result, reasoningText);
                void refreshDashboardStats();
                break;
              }
              case 'error': {
                const errData = event.data as { message?: string };
                const aiMsg: ChatMessage = {
                  id: `ai-${Date.now()}`,
                  content: errData.message || 'Error al procesar la consulta.',
                  sender: 'ai',
                  timestamp: (event.data as { timestamp?: string }).timestamp || new Date().toISOString(),
                };
                addMessage(aiMsg);
                setLoading(false);
                void refreshDashboardStats();
                break;
              }
            }
          },
          (error) => {
            if (!isCurrentGeneration()) return;
            const aiMsg: ChatMessage = {
              id: `ai-error-${Date.now()}`,
              content: `Error de conexión: ${error.message}`,
              sender: 'ai',
              timestamp: new Date().toISOString(),
            };
            addMessage(aiMsg);
            setLoading(false);
            void refreshDashboardStats();
          },
          controller.signal,
          { clarificationChoice: options?.clarificationChoice }
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        const aiMsg: ChatMessage = {
          id: `ai-error-${Date.now()}`,
          content: `Error: ${err instanceof Error ? err.message : 'Desconocido'}`,
          sender: 'ai',
          timestamp: new Date().toISOString(),
        };
        addMessage(aiMsg);
      } finally {
        if (!controller.signal.aborted && isCurrentGeneration()) {
          setLoading(false);
        }
      }
    },
    [
      addMessage,
      setLoading,
      setReasoning,
      setCurrentChart,
      setFocusTrip,
      setFocusTrips,
      setFeatures,
      setRouteData,
      setLivePosition,
      clearRouteLines,
      setChartModalOpen,
      setRoutePanelOpen,
      getSessionId,
      refreshDashboardStats,
    ]
  );

  const handleResult = useCallback(
    (result: SSEResultData, reasoning: string) => {
      const currentState = useChatStore.getState();
      if (currentState.messages.length === 0 && !currentState.isLoading) return;

      const content = result.message || result.answer || '';
      const timestamp = result.timestamp || new Date().toISOString();

      const rawChartData = result.chart || result.chart_data;
      const chartData = rawChartData ? normalizeChartPayload(rawChartData) : undefined;
      const quickReplies = normalizeQuickReplies(result.data?.quick_replies);
      const clarificationType = typeof result.data?.clarification_type === 'string'
        ? result.data.clarification_type
        : undefined;

      const aiMsg: ChatMessage = {
        id: result.assistant_message_id || `ai-${Date.now()}`,
        content,
        sender: 'ai',
        timestamp,
        processingTime: result.execution_time_ms,
        reasoning: reasoning || undefined,
        userMessageId: result.user_message_id,
        assistantMessageId: result.assistant_message_id,
        exportId: result.export_id || undefined,
        chart: chartData || undefined,
        recordCount: result.record_count || undefined,
        records: result.records || undefined,
        active_filters: result.active_filters || undefined,
        clarificationType,
        quickReplies,
      };
      addMessage(aiMsg);
      setLoading(false);
      
      // Update grid/store export status
      if (result.export_id) {
        setCurrentExportId(result.export_id);
      }

      // Auto-trigger download for "exporta a excel" queries
      if (result.data?.trigger_download && result.data?.export_id) {
        triggerExcelDownload(result.data.export_id as string);
        
        // Track export for learning system
        const lastUserQuery = messages[messages.length - 1]?.content || '';
        if (lastUserQuery) {
          trackExport(
            lastUserQuery,
            result.record_count || 0,
            'excel'
          );
        }
      }

      // Update map features
      const mapData = result.map || result.map_data;
      if (mapData?.features?.length) {
        setFeatures(mapData.features);
      }

      // Update chart
      if (chartData) {
        setCurrentChart(chartData);
        setChartModalOpen(true);
      }

      // Focus trip — auto-open route panel for single trip results
      if (result.focus_trip) {
        setFocusTrip(result.focus_trip);
        // Dispatch event so RoutePanel.showRoute() handles it properly
        // (updates currentAlias, position polling, etc.)
        window.dispatchEvent(
          new CustomEvent('show-trip-route', { detail: { alias: result.focus_trip } })
        );
      }
      if (result.focus_trips?.length) {
        setFocusTrips(result.focus_trips);
      }
      if (result.active_filters) {
        setActiveFilters(result.active_filters);
      }
    },
    [addMessage, setLoading, setFeatures, setCurrentChart, setChartModalOpen, setFocusTrip, setFocusTrips, setActiveFilters, setCurrentExportId, messages, trackExport]
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setLoading(false);
  }, [setLoading]);

  return { sendQuery, cancel };
}
